<template>
  <div v-if="showModal" class="modal-backdrop">
    <div class="modal-content">
      <div class="modal-icon">🔔</div>
      <h2>Habilitar Notificaciones</h2>
      
      <p v-if="permissionStatus === 'default'">
        Mantente al día con los últimos eventos y anuncios. ¡Habilita las notificaciones!
      </p>
      <p v-if="permissionStatus === 'denied'">
        Las notificaciones están bloqueadas. Para recibirlas, necesitas habilitarlas manualmente en la configuración de tu navegador.
      </p>

      <div class="modal-actions">
        <button v-if="permissionStatus === 'default'" @click="handleRequestPermission" class="action-btn">
          Habilitar Notificaciones
        </button>
        <button @click="dismissModal" class="dismiss-btn">
          {{ permissionStatus === 'denied' ? 'Entendido' : 'Ahora no' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';

const permissionStatus = ref<NotificationPermission>('default');
const showModal = ref(false);

onMounted(() => {
  // Check for Notification API support
  if (!('Notification' in window)) {
    console.log('This browser does not support desktop notification');
    return;
  }

  permissionStatus.value = Notification.permission;
  const dismissed = sessionStorage.getItem('notifications_prompt_dismissed');

  // Show modal if permission is default or denied, and it hasn't been dismissed
  if (['default', 'denied'].includes(permissionStatus.value) && !dismissed) {
    // Don't show if running as an installed PWA that already got permissions, or on native
    const isPWA = window.matchMedia('(display-mode: standalone)').matches;
    if (permissionStatus.value === 'granted' && isPWA) return;

    setTimeout(() => {
      showModal.value = true;
    }, 2500); // Show after a slightly longer delay than the PWA modal
  }
});

const handleRequestPermission = async () => {
  const permission = await Notification.requestPermission();
  permissionStatus.value = permission;
  if (permission === 'granted') {
    console.log('Notification permission granted.');
    // You might want to trigger token refresh here
  }
  dismissModal(); // Hide modal after user makes a choice
};

const dismissModal = () => {
  sessionStorage.setItem('notifications_prompt_dismissed', 'true');
  showModal.value = false;
};
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  z-index: 1999; /* Slightly lower than PWA modal to avoid overlap issues */
  display: flex;
  justify-content: center;
  align-items: center;
}

.modal-content {
  background-color: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 5px 15px rgba(0,0,0,0.3);
  text-align: center;
  max-width: 90%;
  width: 400px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

.modal-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.modal-content h2 {
  font-size: 1.5rem;
  color: #333;
  margin-bottom: 0.5rem;
}

.modal-content p {
  font-size: 1rem;
  color: #555;
  margin-bottom: 1.5rem;
}

.modal-actions {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.action-btn {
  background: linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%);
  color: white;
  border: none;
  padding: 14px 20px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
}

.action-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
}

.dismiss-btn {
  background: transparent;
  color: #777;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 500;
  font-size: 0.9rem;
  cursor: pointer;
  transition: background-color 0.2s;
}

.dismiss-btn:hover {
  background-color: #f0f0f0;
}
</style>
